// utils
import React from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";

// components
import GrantInfo, { GrantInfoItem } from "../grant-info/grant-info";

export interface GrantDetailProps {
    /** project name */
    header: string;
    /** key grant data shown right below the header */
    info: GrantInfoItem[];
    /** full project text - one item = one paragraph */
    paragraphs: string[];
    /** co-financing statement required by the grant provider */
    cofinancedNote: string;
    /** co-financing banner (EU / ministry logos) */
    bannerSrc: string;
    bannerAlt: string;
    /** "back to grants" button */
    btnBackText: string;
    btnBackUrl: string;
}

/**
 * GrantDetail component
 * reusable full page content of one grant project (styled as the grant tile, but without the tile container)
 * @returns HTML code of grant detail page
 */
export default function GrantDetail(props: GrantDetailProps) {

    // solves broken layout of some pages in build
    const [hasMounted, setHasMounted] = React.useState(false);
    React.useEffect(() => {
      setHasMounted(true);
    }, []);
    if (!hasMounted) {
      return null;
    }

    return (
        <>
            <div className="bg-white navbar-size-compensation">
                <Container className="pt-3 pt-lg-5 pb-5">
                    <Row>
                        {/* co-financing banner */}
                        <Col xs={12} sm={12} className="mt-3 mt-lg-5 fade-in-750-down-to-top">
                            <div className="grants-eu-banner">
                                <img className="grants-eu-banner-img" src={props.bannerSrc} alt={props.bannerAlt}/>
                            </div>
                        </Col>

                        {/* project name */}
                        <Col xs={12} sm={12} className="mt-4 mt-lg-5 fade-in-1000-down-to-top">
                            <h4 className="font-black mb-0">
                                {props.header}
                            </h4>
                        </Col>

                        {/* key grant data */}
                        <Col xs={12} sm={12} className="mt-4 mt-lg-5 fade-in-1250-down-to-top">
                            <GrantInfo info={props.info}/>
                        </Col>

                        {/* full project text */}
                        <Col xs={12} sm={12} className="mt-4 mt-lg-5 fade-in-1500-down-to-top">
                            <hr className="sepparator-horiz-divelit-grey-10"></hr>
                            {props.paragraphs.map((paragraph, index) =>
                                <p className="paragraph-small mt-3 mt-lg-4 mb-0" key={index}>
                                    {paragraph}
                                </p>
                            )}
                        </Col>

                        {/* co-financing statement + back */}
                        <Col xs={12} sm={12} className="mt-4 mt-lg-5 fade-in-1750-down-to-top">
                            <hr className="sepparator-horiz-divelit-grey-10"></hr>
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center mt-3 mt-lg-4">
                                <p className="paragraph-extra-small font-divelit-grey-50 grants-cofinanced-note flex-grow-1 mb-3 mb-lg-0 me-lg-5">
                                    {props.cofinancedNote}
                                </p>
                                <div className="flex-shrink-0">
                                    <Link className="button-w-border paragraph-small" to={props.btnBackUrl}>
                                        <img className="button-arrow grants-button-arrow-back" alt="" aria-hidden="true"></img>{props.btnBackText}
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
}
